// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { get, computed } from '@ember/object';
import { service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
  type HasMany,
} from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { apiAction } from '@mainmatter/ember-api-actions';
import type { Type } from '@warp-drive/core-types/symbols';
import type Store from 'ember-data/store';
import type Agreement from 'invite-sign/models/agreement';
import type ApprovalCheck from 'invite-sign/models/approval-check';
import type FinalSummary from 'invite-sign/models/final-summary';
import type Flow from 'invite-sign/models/flow';
import type FlowQuestionnaire from 'invite-sign/models/flow-questionnaire';
import type Invite from 'invite-sign/models/invite';
import type Location from 'invite-sign/models/location';
import type Questionnaire from 'invite-sign/models/questionnaire';
import type SignInField from 'invite-sign/models/sign-in-field';
import type UserDocumentTemplate from 'invite-sign/models/user-document-template';
import type UserDocumentTemplateConfiguration from 'invite-sign/models/user-document-template-configuration';
import type LocalizationService from 'invite-sign/services/localization';
import type SessionService from 'invite-sign/services/session';
import type { VisitorEntry } from 'invite-sign/services/session';
import { sortByPosition } from 'invite-sign/utils/sorting';
import type { ModelData, SingleResponse } from 'jsonapi/response';

export type EntryAttributes = {
  host: string;
  name: string;
  'flow-id': number | string;
  'user-data': { [key: string]: string };
} & ModelData<Entry>;

/**
 * Shape shared by PreviousEntry.entry and SearchedReturningVisitor.
 * Returned by the /entries/find-previous API when a returning visitor is matched.
 */
export type ReturningVisitorData = {
  id: Entry['id'];
  attributes: EntryAttributes;
  validSignedAgreements: unknown[];
  completedUserDocumentTemplates: unknown[];
  completedQuestionnaireIds: unknown[];
  /** When true, the visitor's previous visit already captured an ID scan, so the step can be skipped. */
  skipIdScan?: boolean;
  relationships: {
    flow: {
      data: {
        type: string;
        id: Flow['id'];
      };
    };
  };
};

export interface PreviousEntry {
  entry: ReturningVisitorData;
  'valid-signed-agreements': unknown[];
  'completed-user-document-templates': unknown[];
  'completed-questionnaires': unknown[];
  'skip-id-scan'?: boolean;
}

export default class Entry extends Model {
  declare [Type]: 'entry';

  @service declare localization: LocalizationService;
  @service declare session: SessionService;
  @service declare store: Store;

  @belongsTo('location', { async: false, inverse: null }) declare location: Location;
  @belongsTo('flow', { async: true, inverse: null }) declare flow: AsyncBelongsTo<Flow> | Flow;
  @belongsTo('invite', { async: true, inverse: null }) declare invite: AsyncBelongsTo<Invite>;
  @belongsTo('final-summary', { async: false, inverse: null }) declare finalSummary: FinalSummary | null;
  @hasMany('agreement', { async: true, inverse: null }) declare agreements: AsyncHasMany<Agreement>;
  @hasMany('approval-check', { async: true, inverse: 'entry' }) declare approvalChecks: AsyncHasMany<ApprovalCheck>;
  @hasMany('sign-in-field', { async: false, inverse: 'entry' }) declare signInFields: HasMany<SignInField>;

  @attr('string') declare email: string;
  @attr('string') declare fullName: string;
  @attr() declare photo: unknown;
  @attr('string', { defaultValue: 'en' }) declare selectedLanguage: string;
  @attr('date') declare signedOutAt: Date;
  @attr('number', { defaultValue: 0 }) declare additionalGuests: number;

  @attr('date') declare finalizedAt: Date;
  @attr('date') declare signedInAt: Date;
  @attr('date') declare createdAt: Date;

  @attr({ defaultValue: () => ({ 'place-id': '' }) }) declare locality: { 'place-id'?: string };

  @tracked completedUserDocumentTemplates: string[] | null = null;
  @tracked completedQuestionnaireIds: string[] | null = null;
  @tracked idScanSessionId: string | null = null;
  validSignedAgreements: string[] | null = null;

  @computed('signInFields.@each.value')
  get hostName(): string | undefined {
    const hostSignInField = this.signInFields.find((field) => field.identifier === 'host');
    return hostSignInField?.value;
  }

  @computed('approvalChecks.{content,@each.status}')
  get approved(): boolean {
    return this.approvalChecks.content?.slice().every(({ status }) => status === 'approved') ?? false;
  }

  get incompleteVisitorDocumentConfigurations(): UserDocumentTemplateConfiguration[] {
    // eslint-disable-next-line ember/no-get
    const visitorDocumentTemplateConfigurations =
      <UserDocumentTemplateConfiguration[] | null>get(this, 'flow.visitorDocumentTemplateConfigurations') || [];
    const completedDocumentIds = (this.completedUserDocumentTemplates || []).map((userDocumentTemplate) =>
      userDocumentTemplate.toString(),
    );

    return visitorDocumentTemplateConfigurations
      .filter((config) => {
        return !completedDocumentIds.includes(config.userDocumentTemplate.id);
      })
      .sort((a, b) => sortByPosition(a.userDocumentTemplate, b.userDocumentTemplate));
  }

  get incompleteUserDocumentTemplates(): UserDocumentTemplate[] {
    return this.incompleteVisitorDocumentConfigurations.map((config) => config.userDocumentTemplate);
  }

  get incompleteQuestionnaires(): Questionnaire[] {
    const completedQuestionnaireIds = (this.completedQuestionnaireIds || []).map((questionnaire) =>
      questionnaire.toString(),
    );

    // eslint-disable-next-line ember/no-get
    const questionnaires = (<FlowQuestionnaire[]>(get(this, 'flow.flowQuestionnaires') || []))
      .slice()
      .toSorted((a, b) => a.position - b.position)
      .map((fq) => fq.questionnaire)
      .filter((questionnaire) => {
        return !completedQuestionnaireIds.includes(questionnaire.id!);
      });

    return questionnaires;
  }

  get foundInvite(): Invite | null {
    // @ts-expect-error - the types don't like the use of the string arg to `.belongsTo()` but it does work
    return <Invite | null>this.belongsTo('invite').value();
  }

  async finalize(): Promise<void> {
    const payload = <SingleResponse<Entry>>this.serialize();
    const { attributes } = payload.data;

    if (attributes) {
      // @ts-expect-error - the Entry serializer sets 'photo-base64' from 'photo'; it doesn't _really_ exist on this model
      delete attributes['photo-base64'];
      // @ts-expect-error - `idScanSessionId` isn't a true attribute (it's never received from the API) but should be sent up when finalizing
      attributes['id-scan-session-id'] = this.idScanSessionId;
    }

    const query = new URLSearchParams({
      include: 'final-summary',
      locale: this.localization.languageCode,
    });

    const response = await apiAction<SingleResponse<Entry>>(this, {
      data: payload,
      method: 'POST',
      path: `finalize?${query}`,
    });

    // @ts-expect-error - ember-data's types for `pushPayload` don't match the documentation. It works to only pass the payload, not a model name, but the types don't define that signature.
    this.store.pushPayload(response);
    this.session.finalizeEntry(this);
  }

  get hasValidSignInFields(): boolean {
    // We don't need to validate the host field or hidden conditional fields
    return this.signInFields
      .map((field) => field.isName || !field.isVisible || field.validate())
      .every((value) => value);
  }

  async signOut(locationId: string): Promise<void> {
    const payload = {
      id: this.id,
      full_name: this.fullName,
      ['location-id']: locationId,
    };

    await apiAction(this, {
      adapterOptions: { useV2Api: true },
      data: payload,
      method: 'POST',
      path: 'sign-out',
    });
  }

  async signOutGuest({ id, locationId, name, signedInAt, createdAt }: VisitorEntry): Promise<void> {
    const payload = {
      id,
      full_name: name,
      'location-id': locationId,
      'finalized-at': new Date(),
      'signed-in-at': signedInAt,
      'created-at': createdAt,
    };

    await apiAction(this, {
      adapterOptions: { useV2Api: true },
      data: payload,
      method: 'POST',
      path: 'sign-out',
    });
  }

  async findPrevious(options: object): Promise<PreviousEntry> {
    return await apiAction(this, {
      adapterOptions: { useV2Api: true },
      data: options,
      method: 'GET',
      path: 'find-previous',
      requestType: 'findAll',
    });
  }
}
